import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Spinner, Text, VStack } from '@chakra-ui/react'
import axios from 'axios'
import { grantApi } from '../../lib/api'
import { writeGrantMirror } from '../../lib/grantMirror'
import { storeGrantSessionToken } from '../../lib/session/grantSessionToken'
import { clearReturnCredential } from '../../lib/returnCredential'
import { TermsAckPanel, TERMS_VERSION } from '../../components/canvasser/TermsAckPanel'
import type { GrantIdentity } from '../../types/grant'

type Phase = 'checking' | 'terms' | 'submitting' | 'invalid' | 'ratelimited' | 'offline'

function deviceLabel(): string {
  return (navigator.userAgent || 'device').slice(0, 60)
}

export function RedeemPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [phase, setPhase] = useState<Phase>('checking')
  // The mount probe below mints a NEW GrantSession on the server for every redeem POST
  // (CreateSessionAsync always INSERTs). React StrictMode double-invokes the effect, and a
  // future re-render could re-run it — so fire the probe exactly ONCE per token, or a single
  // welcome-back / magic-link entry shows up as two "devices" in the session dashboard.
  const probedTokenRef = useRef<string | null>(null)
  const cancelledRef = useRef(false)

  const onIdentity = async (identity: GrantIdentity) => {
    // [Native B1] The gs_ token goes to secure storage before anything navigates (web: no-op).
    await storeGrantSessionToken(identity)
    // Defense in depth: destructure only the id fields — never pass the full
    // GrantIdentity (which also carries subjectFirstName/subjectLastName)
    // through to the mirror sink. See grantMirror.ts for the primary guard.
    const { grantId, kind, subjectContactId, ownerCommitteeId } = identity
    writeGrantMirror({ grantId, kind, subjectContactId, ownerCommitteeId })
    navigate('/canvasser', { replace: true })
  }

  const handleError = (err: unknown) => {
    // Tolerate both real AxiosError instances and plain `{ response: { status } }`
    // shapes (e.g. from mocked rejections in tests) — read the status structurally
    // rather than gating on axios.isAxiosError, which is false for the latter.
    const status = (err as { response?: { status?: number } } | undefined)?.response?.status
    if (status === 404) {
      // [L11 P2] The durable token no longer resolves (grant revoked/blocked/expired). Drop the
      // stored re-entry credential so it can't loop; the person falls back to a fresh join or
      // organizer re-link. ONLY on 404 — an offline/429 is transient and must keep the credential.
      clearReturnCredential()
      return setPhase('invalid')
    }
    if (status === 429) return setPhase('ratelimited')
    if (axios.isAxiosError(err) && !err.response) return setPhase('offline')
    setPhase('invalid')
  }

  // Phase 1: probe (no acceptance). Terms already accepted -> Identity directly.
  useEffect(() => {
    if (!token) { setPhase('invalid'); return }
    // cancelledRef is reset at the top of every effect run, so StrictMode's synthetic
    // unmount (which sets it true in cleanup) doesn't cancel the surviving run's async
    // completion — only a REAL unmount, where the cleanup runs with no follow-up mount, does.
    cancelledRef.current = false
    if (probedTokenRef.current !== token) {
      probedTokenRef.current = token
      grantApi.redeem({ token })
        .then(({ data }) => {
          if (cancelledRef.current) return
          if (data.identity) return onIdentity(data.identity).catch(handleError)
          if (data.termsRequired) return setPhase('terms')
          setPhase('invalid')
        })
        .catch((e) => { if (!cancelledRef.current) handleError(e) })
    }
    return () => { cancelledRef.current = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const accept = async () => {
    setPhase('submitting')
    try {
      const { data } = await grantApi.redeem({ token, acceptTerms: true, termsVersion: TERMS_VERSION, deviceLabel: deviceLabel() })
      if (data.identity) return await onIdentity(data.identity)
      setPhase('invalid')
    } catch (e) {
      handleError(e)
    }
  }

  if (phase === 'terms' || phase === 'submitting')
    return <TermsAckPanel onAccept={accept} isSubmitting={phase === 'submitting'} />
  if (phase === 'invalid')
    return <Terminal>This link is invalid or has expired. Ask your organizer for a new one.</Terminal>
  if (phase === 'ratelimited')
    return <Terminal>Too many attempts. Please try again in a minute.</Terminal>
  if (phase === 'offline')
    return <Terminal>You're offline. Connect to the internet to sign in.</Terminal>
  return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
}

/**
 * A dead-end redeem state (bad/expired token, rate-limited, offline). Always offers a way off
 * the screen — the front-door chooser at "/" — so a signed-out device isn't stranded here
 * without re-typing the URL.
 */
function Terminal({ children }: { children: React.ReactNode }) {
  return (
    <VStack py={12} px={6} gap={4}>
      <Text color="fg.muted" textAlign="center">{children}</Text>
      <Button asChild colorPalette="brand" minH="44px">
        <RouterLink to="/">Back to start</RouterLink>
      </Button>
    </VStack>
  )
}
